/**
 * runRecord/types — wire shapes for the 26742 run-record page and its 26747
 * touchpoint counts, as published by the substrate's `get_run_decisions`
 * tool (see tasks/overnight-2026-09-09-plan.md, "Cross-worktree contracts").
 *
 * WHY THIS FILE EXISTS SEPARATELY FROM workflows.service.server.tsx:
 * `getRunDecisions` is being added to that module by a different agent
 * working the SAME worktree (WT-P2, sub-agent P2-C) in parallel. Restating
 * the shape here — rather than blocking on their commit — lets this half of
 * the story build and test independently; once their method lands, its
 * return type should be this one (or a structurally-compatible one) and this
 * file's `WorkflowsServiceWithRunDecisions` cast in the route loader can be
 * deleted in favor of the real method signature.
 */

/** Mirrors the substrate's `caller_surface_var` vocabulary (obs/surface.py). */
export type RunDecisionSurface =
  | "outlook_addin"
  | "ora_web"
  | "admin_route"
  | "mcp"
  | "job"
  | "unknown";

/** Human label for a surface value. Only the two end-user surfaces get a
 *  distinct label per the contract ("render `via Outlook add-in` / `via
 *  ora`"); the others get a short generic phrase rather than the raw wire
 *  value, and `unknown`/unrecognised values render nothing (the caller omits
 *  the "via ..." clause entirely). */
export function surfaceLabel(surface: RunDecisionSurface | string | null | undefined): string | null {
  switch (surface) {
    case "outlook_addin":
      return "Outlook add-in";
    case "ora_web":
      return "ora";
    case "admin_route":
      return "an admin action";
    case "mcp":
      return "an automation";
    case "job":
      return "a scheduled job";
    default:
      return null;
  }
}

/** The actor of a governance row, as the PRODUCT API projects it — not as the
 *  substrate emits it. The substrate writes one prefixed string
 *  (`person:<oid>`, or a `system:*` / `service:*` prefix); the API splits it on
 *  the first `:` (`RunDecisionActor.Parse`) and resolves `name` for `person`
 *  actors server-side in `GetRunDecisionsHandler`, the same way it resolves
 *  HITL `answered_by`. The UI therefore never sees the raw prefixed string, and
 *  there is no sibling `actor_name` key — the name lives on the actor. */
export interface RunDecisionActor {
  kind: string;
  identifier: string;
  name: string | null;
}

/** One governance row from `decision_log` (26730), as `get_run_decisions`
 *  projects it for a single run. */
export interface RunDecision {
  ts: string;
  event_type: string;
  actor: RunDecisionActor;
  surface: RunDecisionSurface | string | null;
  authority: string | null;
  subject_type: string | null;
  subject_id: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  reason: string | null;
  trace_id: string | null;
}

/** One HITL Q&A row (`workflow_instance_hitl_events`), as the same tool
 *  projects it — the substrate-authoritative sibling of the events this page
 *  can also derive from `getWorkflowHitlHistory` alone when this tool is
 *  unavailable. */
export interface RunHitlAnswer {
  sequence: number;
  step_id: string;
  phase_id: string | null;
  event_type: string;
  asked_at: string;
  answered_at: string | null;
  answered_by: string | null;
  answered_by_name: string | null;
  surface: RunDecisionSurface | string | null;
  prompt_text: string | null;
  response: Record<string, unknown> | null;
  artifacts_snapshot: string[];
}

/** One notification-lifecycle row (`hitl_notifications`, 26731/26734). */
export interface RunNotification {
  decision_key: string | null;
  decision_id: string | null;
  recipient: string | null;
  status: string;
  attempts: number;
  failure_reason: string | null;
  failure_kind: string | null;
  link: string | null;
  subject: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** `null` until 0064's column exists (contract note) — a genuinely unknown
   *  state, distinct from `false`. */
  source_message_lost: boolean | null;
}

/** The 26747 touchpoint rollup for this one run. */
export interface RunTouchpointSummary {
  planned: number;
  exception: number;
  attention_seconds: number;
}

/** `get_run_decisions(run_id)` response, forwarded by
 *  `GET /api/workflows/instances/{id}/decisions` (P2-C). */
export interface GetRunDecisionsResponse {
  run_id: string;
  workflow_id: string;
  status: string;
  summary: string | null;
  decisions: RunDecision[];
  hitl_answers: RunHitlAnswer[];
  notifications: RunNotification[];
  touchpoints: RunTouchpointSummary;
  counts: Record<string, number>;
  /** The tool ran but is telling us its own answer is incomplete — treated
   *  identically to "the call failed" by the loader (contracts table). */
  degraded?: boolean;
}

/**
 * The subset of `createWorkflowsService()`'s return value this feature reads
 * that has not landed yet in `workflows.service.server.tsx` (owned by P2-C).
 * The route casts the real service through this interface so calling a
 * method that may not exist yet degrades at the call site instead of being a
 * compile-time dependency on another agent's in-flight commit.
 */
export interface WorkflowsServiceWithRunDecisions {
  getRunDecisions?: (instanceId: string) => Promise<GetRunDecisionsResponse>;
}
